import React, { useState } from 'react';
import { API_BASE_URL } from '../config';
import { canUploadCoursePlan, getSelectedYear, isReadOnly } from '../config/academicYear';
import './ProgramOutcomeUpload.css';

const AdminCourseUpload = ({ adminToken, onBack }) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setResult(null);
            setError(null);
        }
    };

    const handleUpload = async (mode = 'preview') => {
        if (!file) {
            setError('Lütfen bir Excel dosyası seçin.');
            return;
        }
        if (isReadOnly() && !canUploadCoursePlan()) {
            setError('Bu akademik yıla ders planı yüklenemez. Aktif veya hazırlık yılını seçin.');
            return;
        }

        const academicYear = getSelectedYear();
        if (!academicYear) {
            setError('Derslerin yükleneceği akademik yılı seçin.');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', mode);
        if (mode === 'apply') formData.append('confirmation', 'DERS_PLANINI_YUKLE');

        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/courses/upload-excel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${adminToken}`,
                    'X-Academic-Year': academicYear
                },
                body: formData
            });

            const data = await response.json();

            if (response.ok) {
                setResult(data);
                if (mode === 'apply') {
                    setFile(null);
                    const fileInput = document.querySelector('input[type="file"]');
                    if (fileInput) fileInput.value = '';
                }
            } else {
                setError(data.error || 'Dosya yüklenirken bir hata oluştu.');
            }
        } catch (err) {
            setError('Sunucuya bağlanırken bir hata oluştu.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const SheetIcon = () => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
            <line x1="12" y1="11" x2="12" y2="19" />
        </svg>
    );

    const UploadIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
    );

    const AlertIcon = () => (
        <svg className="po-message__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    );

    const CheckIcon = () => (
        <svg className="po-message__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    );

    const columns = [
        { col: 'B', label: 'Ders Kodu' },
        { col: 'D', label: 'Ders Adı' },
        { col: 'F', label: 'Öğretim Elemanı' },
        { col: 'N', label: 'Program Adı' },
        { col: 'P', label: 'Sınıf (örn: 1. sınıf)' },
        { col: 'L', label: 'Dönem (örn: 1-Güz)' },
        { col: 'U', label: 'Teorik Saat' },
        { col: 'W', label: 'Laboratuvar Saat' },
        { col: 'T', label: 'Kredi' },
        { col: 'X', label: 'AKTS' },
    ];

    const isExcelFile = (name) => /\.(xlsx|xls)$/i.test(name);

    return (
        <div className="po-page">
            <div className="po-page-header">
                <button type="button" className="po-page-header__back" onClick={onBack}>
                    ← Geri
                </button>
                <h1 className="po-page-header__title">Excel ile Ders Yükleme</h1>
                <span className="po-page-header__subtitle">
                    Yönetim Paneli · {getSelectedYear() || 'Akademik yıl seçilmedi'} · Excel (.xlsx, .xls)
                </span>
            </div>

            <div className="po-container">
                <div className="po-card">
                    <div className="po-card__header">
                        <h2 className="po-card__title">Belge Yükleme</h2>
                    </div>
                    <div className="po-card__body">
                        <label
                            className={`po-dropzone ${isDragging ? 'is-active' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setIsDragging(false);
                                const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
                                if (dropped && isExcelFile(dropped.name)) {
                                    setFile(dropped);
                                    setResult(null);
                                    setError(null);
                                } else if (dropped) {
                                    setError('Lütfen .xlsx veya .xls uzantılı bir Excel dosyası seçin.');
                                }
                            }}
                        >
                            <span className="po-dropzone__icon"><SheetIcon /></span>
                            <p className="po-dropzone__primary">
                                Excel dosyasını buraya sürükleyin
                            </p>
                            <p className="po-dropzone__secondary">
                                veya seçmek için tıklayın · .xlsx, .xls
                            </p>
                            {file && <div className="po-dropzone__filename">{file.name}</div>}
                            <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
                        </label>

                        <div className="po-actions">
                            <button
                                type="button"
                                className="po-button po-button--primary"
                                onClick={() => handleUpload('preview')}
                                disabled={!file || loading}
                            >
                                <UploadIcon />
                                {loading ? 'İnceleniyor…' : 'Excel’i Önizle'}
                            </button>
                            {result?.mode === 'preview' && (
                                <button
                                    type="button"
                                    className="po-button po-button--primary"
                                    onClick={() => handleUpload('apply')}
                                    disabled={!file || loading || !(result.created_courses + result.updated_courses > 0)}
                                >
                                    <UploadIcon />
                                    {loading ? 'Yükleniyor…' : 'Onayla ve Ders Planını Yükle'}
                                </button>
                            )}
                        </div>

                        {error && (
                            <div className="po-message po-message--error" role="alert">
                                <AlertIcon />
                                <span><span className="po-message__title">Hata:</span>{error}</span>
                            </div>
                        )}

                        {result && (
                            <div className="po-message po-message--success" role="status">
                                <CheckIcon />
                                <span>
                                    <span className="po-message__title">{result.mode === 'preview' ? 'Önizleme:' : 'Başarılı:'}</span>
                                    {result.message || 'Veriler başarıyla yüklendi.'}
                                    <p style={{ marginTop: 10, marginBottom: 0 }}>
                                        Yeni: {result.created_courses || 0} · Güncellenecek: {result.updated_courses || 0} ·
                                        İzlecesi aktarılacak: {result.inherited_syllabi || 0} ·
                                        Boş izlence: {result.new_without_syllabus || 0} ·
                                        Akademisyen ataması: {result.successful_assignments || 0}
                                    </p>
                                    {result.missing_academicians?.length > 0 && (
                                        <>
                                            <p style={{ marginTop: 10, marginBottom: 6, fontWeight: 700 }}>
                                                Ataması daha sonra tamamlanacak akademisyenler:
                                            </p>
                                            <p style={{ marginTop: 0, marginBottom: 6, color: '#92400e' }}>
                                                Bu kayıtlar ders planının ve izlencelerin yüklenmesini engellemez.
                                                Akademisyenleri Analiz sistemine ekledikten sonra ders atamalarını tamamlayabilirsiniz.
                                            </p>
                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem' }}>
                                                {result.missing_academicians.map((item, i) => (
                                                    <li key={i}>{item.course_code} · {item.academician}: {item.reason}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    {result.deferred_elective_assignments?.length > 0 && (
                                        <p style={{ marginTop: 10, marginBottom: 0, color: '#92400e' }}>
                                            {result.deferred_elective_assignments.length} seçmeli alt ders akademisyeni saklandı;
                                            ders bağımsız course_id aldığında atama otomatik tamamlanacaktır.
                                        </p>
                                    )}
                                    {result.errors && result.errors.length > 0 && (
                                        <>
                                            <p style={{ marginTop: 10, marginBottom: 6, fontWeight: 700 }}>
                                                {result.mode === 'preview' ? 'Aşağıdaki hatalı satırlar atlanacak; geçerli dersleri yükleyebilirsiniz:' : 'Aşağıdaki hatalı satırlar atlandı:'}
                                            </p>
                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem', maxHeight: 200, overflowY: 'auto' }}>
                                                {result.errors.map((err, i) => (
                                                    <li key={i}>{err}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="po-card">
                    <div className="po-card__header">
                        <h2 className="po-card__title">Excel Sütun Yapısı</h2>
                    </div>
                    <div className="po-card__body">
                        <p style={{ margin: '0 0 14px', color: '#475569', fontSize: '0.9rem' }}>
                            Excel dosyasındaki veriler aşağıdaki sütunlardan okunur.
                        </p>
                        <ul className="po-format-list">
                            {columns.map(({ col, label }) => (
                                <li key={col}>
                                    <strong>{col} Sütunu</strong>
                                    {label}
                                </li>
                            ))}
                        </ul>
                        <div className="po-notes">
                            <p>· <strong>Ön koşul:</strong> Seçili yılın akademisyen tam listesi önce Analiz sistemine yüklenmelidir. EBS yeni akademisyen hesabı açmaz.</p>
                            <p>· Dersler yalnızca üst menüde seçili <strong>akademik yıla</strong> yüklenir.</p>
                            <p>· Önceki yılda aynı program ve ders kodu varsa izlence kopyalanır; PÇ eşleştirmeleri kopyalanmaz.</p>
                            <p>· <strong>Program Adı</strong>, sistemdeki program ismiyle tam eşleşmelidir.</p>
                            <p>· <strong>Dönem</strong> bilgisi, Sınıf ve Güz/Bahar metinlerine göre otomatik hesaplanır.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCourseUpload;
